import React, { useLayoutEffect, useRef, useState } from "react";
import { Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  getDomainMigrationBrandKey,
  getDomainMigrationNotice,
} from "../../utils/domainMigrationNotice";

import "./DomainMigrationBanner.css";

const WARNING_ICON =
  "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_warning.svg";
const CLOSE_ICON =
  "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/close.svg";

const BODY_CLASS = "has-domain-migration-banner";
const HEIGHT_VAR = "--domain-migration-banner-height";
const DISMISSED_KEY_PREFIX = "domainMigrationNoticeDismissed_";
// Set by Welcome.jsx while the age-confirm dialog is covering the screen.
const AGE_CONFIRM_BODY_CLASS = "age-confirm-modal-open";

// Full-screen call/live views already use the top of the viewport.
const HIDDEN_PATH_PREFIXES = ["/golive", "/call-f2f", "/user-call-f2f"];

// Auth layouts have no fixed app header. Keep the banner in document flow so
// iOS pull-to-refresh rubber-bands it with the page instead of leaving a blank
// slot behind position:fixed.
const IN_FLOW_PATH_PREFIXES = [
  "/login",
  "/sign-in",
  "/register",
  "/verify",
  "/reset-password",
];

const matchesPathPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const isHiddenRoute = (pathname) =>
  HIDDEN_PATH_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix));

const isInFlowRoute = (pathname) =>
  IN_FLOW_PATH_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix));

const clearBannerOffset = () => {
  document.body.classList.remove(BODY_CLASS);
  document.documentElement.style.setProperty(HEIGHT_VAR, "0px");
};

const isDismissed = (brandKey) => {
  try {
    return localStorage.getItem(`${DISMISSED_KEY_PREFIX}${brandKey}`) === "true";
  } catch (e) {
    return false;
  }
};

const DomainMigrationBanner = () => {
  const notice = getDomainMigrationNotice();
  const brandKey = getDomainMigrationBrandKey();
  const { pathname } = useLocation();
  const bannerRef = useRef(null);
  const isDark = useSelector((state) => state.theme.isDark);
  const inFlow = isInFlowRoute(pathname);
  const [dismissed, setDismissed] = useState(
    () => notice?.dismissible && isDismissed(brandKey)
  );
  const [ageConfirmOpen, setAgeConfirmOpen] = useState(
    () => document.body.classList.contains(AGE_CONFIRM_BODY_CLASS)
  );
  const shouldShow =
    Boolean(notice?.message) &&
    !dismissed &&
    !ageConfirmOpen &&
    !isHiddenRoute(pathname);

  const handleDismiss = () => {
    try {
      localStorage.setItem(`${DISMISSED_KEY_PREFIX}${brandKey}`, "true");
    } catch (e) {
      // Ignore storage errors (e.g. private browsing) — banner will just reappear next visit.
    }
    setDismissed(true);
  };

  useLayoutEffect(() => {
    const syncAgeConfirmOpen = () =>
      setAgeConfirmOpen(document.body.classList.contains(AGE_CONFIRM_BODY_CLASS));

    syncAgeConfirmOpen();

    const observer = new MutationObserver(syncAgeConfirmOpen);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!shouldShow) {
      clearBannerOffset();
      return clearBannerOffset;
    }

    const updateHeight = () => {
      // In-flow routes already occupy space in the document, so extra offsets
      // would double the gap. Keep the body class so leftover header padding
      // on .landing-main-wrapper is cleared (0px).
      const height = inFlow ? 0 : bannerRef.current?.offsetHeight || 0;
      document.body.classList.add(BODY_CLASS);
      document.documentElement.style.setProperty(HEIGHT_VAR, `${height}px`);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    let observer;
    if (typeof ResizeObserver !== "undefined" && bannerRef.current) {
      observer = new ResizeObserver(updateHeight);
      observer.observe(bannerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateHeight);
      observer?.disconnect();
      clearBannerOffset();
    };
  }, [shouldShow, notice?.message, inFlow]);

  if (!shouldShow) return null;

  return (
    <div
      ref={bannerRef}
      className={`domain-migration-banner${isDark ? " domain-migration-banner--dark" : ""}${inFlow ? " domain-migration-banner--in-flow" : ""}`}
      role="status"
    >
      <div className="domain-migration-banner__inner">
        <Image
          src={WARNING_ICON}
          alt=""
          className="domain-migration-banner__icon"
        />
        <p className="domain-migration-banner__message mb-0">
          {notice.message}
          {notice.linkHref ? (
            <>
              {" "}
              <a
                href={notice.linkHref}
                className="pink domain-migration-banner__link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {notice.linkLabel || "Learn more"}
              </a>
            </>
          ) : null}
        </p>
        {notice.dismissible ? (
          <button
            type="button"
            className="domain-migration-banner__close"
            aria-label="Dismiss notice"
            onClick={handleDismiss}
          >
            <Image src={CLOSE_ICON} alt="" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default DomainMigrationBanner;
